import React from 'react'

import { EventSegment } from './types'
import { useThemedColours } from 'hooks/useTheme'

interface EventPillProps {
  segment: EventSegment
  onClick: (rowId: string) => void
}

// Default tint when no row-level colour rule matched. Matches Morta's calendar
// icon colour. When a row-level rule does match, colours come from the rule
// (see ../helpers.ts `getRowColour`) and are applied via inline styles below.
const DEFAULT_PILL_CLASSES = 'bg-really-light-yellow border-alert text-orange'

const EventPill: React.FC<EventPillProps> = ({ segment, onClick }) => {
  const themed = useThemedColours()
  const isBar = segment.isMultiDay
  const colour = segment.colour

  const leftRounded = !segment.isContinuationFromPrev
  const rightRounded = !segment.isContinuedNext
  const radius = [leftRounded ? 'rounded-l-md' : '', rightRounded ? 'rounded-r-md' : ''].filter(Boolean).join(' ')

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick(segment.eventId)
    }
  }

  const ruleStyle: React.CSSProperties | undefined = colour
    ? {
        backgroundColor: themed.fill(colour.backgroundColour),
        color: themed.text(colour.fontColour, colour.backgroundColour),
        borderColor: themed.fill(colour.backgroundColour),
        fontWeight: colour.fontBold ? 700 : undefined,
        fontStyle: colour.fontItalic ? 'italic' : undefined,
        textDecoration: colour.fontUnderline ? 'underline' : undefined
      }
    : undefined

  const baseClasses = colour ? '' : DEFAULT_PILL_CLASSES
  const timeLabelStyle: React.CSSProperties | undefined = colour
    ? { color: themed.text(colour.fontColour, colour.backgroundColour), opacity: 0.8 }
    : undefined

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(segment.eventId)}
      onKeyDown={handleKeyDown}
      style={ruleStyle}
      className={`flex items-center justify-between gap-1 px-2 py-0.5 text-xs font-medium border cursor-pointer hover:brightness-95 ${baseClasses} ${radius} overflow-hidden whitespace-nowrap`}
      title={segment.label}
    >
      <span className="truncate">{segment.label}</span>
      {segment.timeLabel && !isBar && (
        <span style={timeLabelStyle} className={`flex-shrink-0 font-normal ${colour ? '' : 'text-muted'}`}>
          {segment.timeLabel}
        </span>
      )}
    </div>
  )
}

export default EventPill
